import React, { useEffect, useState } from "react";
import { Avatar, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import AccountSecurity from "../../../shares/account/AccountSecurity";
import { clearSession } from "../../../../auth/session";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PersonIcon from "@mui/icons-material/Person";
import LockResetIcon from "@mui/icons-material/LockReset";
import useFetch from "../../../../hooks/useFetch";
import {
    PageHeader,
    SectionCard,
    Skeleton,
} from "../../../shares/dashboard/DashboardWidgets";

/**
 * Profile: identity and password.
 *
 * Three things were wrong beyond the styling:
 *  - validation errors rendered as bare English strings ("password_old is
 *    required") in a Persian UI, below the field rather than attached to it;
 *  - the page fetched student-panel/:id and never showed any of it, so the
 *    student's own name was invisible on their profile;
 *  - an `editable` flag was wired to every field's disabled prop but never set
 *    to anything but true.
 *
 * The avatar PATCH and password POST flows are unchanged.
 */
const Profile = () => {
    const [student, setStudent] = useState("");
    const [avatarFile, setAvatarFile] = useState("");
    const [avatarCall, setAvatarCall] = useState("");
    const [password0, setPassword0] = useState("");
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");
    const [call, setCall] = useState();

    const getStudent = useFetch({
        method: "GET",
    safeRead: true,
        url: "student-panel/",
        noHeader: false,
        trigger: true,
        setter: setStudent,
        errMessage: (error) =>
            toast.error(error.message, {
                position: "bottom-left",
                rtl: true,
                autoClose: 2000,
            }),
    });

    const passChange = useFetch({
        method: "POST",
        url: `student-panel/change-password/`,
        noHeader: false,
        trigger: false,
        data: call,
        errMessage: (error) =>
            toast.error(error.message, {
                position: "bottom-left",
                rtl: true,
                autoClose: 2000,
            }),
    });

    const avatarChange = useFetch({
        method: "PATCH",
        url: `student-panel/change-avatar/`,
        noHeader: false,
        trigger: false,
        caller: getStudent,
        data: avatarCall,
        errMessage: (error) =>
            toast.error(error.message, {
                position: "bottom-left",
                rtl: true,
                autoClose: 2000,
            }),
    });

    const handleAvatarChange = (event) => {
        setAvatarFile(event.target.files[0]);
    };

    useEffect(() => {
        const formData = new FormData();
        formData.append("avatar", avatarFile);
        setAvatarCall(formData);
        avatarFile && avatarChange.reFetch();
    }, [avatarFile]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();

    const onSubmit = () => {
        const formData = new FormData();
        formData.append("old_password", password0);
        formData.append("new_password", password);
        setCall(formData);
        passChange.reFetch();
    };

    useEffect(() => {
        if (passChange.status >= 200 && passChange.status < 300) {
            clearSession();
            toast.success("رمز عبور تغییر کرد.", {
                position: "bottom-left",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                progress: undefined,
                theme: "light",
                rtl: true,
            });
            setPassword("");
            setPassword0("");
            setPassword2("");
        }
    }, [passChange.status]);

    const details = student?.studentuser;
    const fullName = [student?.first_name, student?.last_name]
        .filter(Boolean)
        .join(" ");
    const group = [
        details?.student_grade,
        details?.student_type,
        details?.student_gnum,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className="flex flex-col gap-xl pb-4xl">
            <AccountSecurity />
            <PageHeader
                title="پروفایل"
                description="اطلاعات حساب و رمز عبورت"
                icon={PersonIcon}
            />

            <div className="grid grid-cols-3 gap-xl max-md:grid-cols-1">
                <SectionCard title="حساب من" icon={PersonIcon} index={1}>
                    <div className="flex flex-col items-center gap-md text-center">
                        <label
                            htmlFor="avatar-upload"
                            className="group relative cursor-pointer rounded-pill focus-within:ring-2 focus-within:ring-primary"
                        >
                            <Avatar
                                alt={fullName || "آواتار"}
                                src={details?.avatar || undefined}
                                sx={{ width: "12rem", height: "12rem" }}
                            />
                            <span className="absolute bottom-0 end-0 flex h-[3.6rem] w-[3.6rem] items-center justify-center rounded-pill border-2 border-surface bg-primary text-white transition-colors duration-150 group-hover:bg-primary-hover">
                                <PhotoCameraIcon sx={{ fontSize: "1.8rem" }} />
                            </span>
                            <input
                                id="avatar-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleAvatarChange}
                                className="sr-only"
                            />
                        </label>

                        {getStudent.loading && !student ? (
                            <Skeleton className="h-[2rem] w-[16rem]" />
                        ) : (
                            <div>
                                <p className="text-lg font-semibold text-ink">
                                    {fullName || "دانش‌آموز"}
                                </p>
                                {group && (
                                    <p className="mt-2xs text-sm text-ink-subtle">
                                        {group}
                                    </p>
                                )}
                            </div>
                        )}

                        <p className="text-sm text-ink-subtle">
                            برای تغییر عکس، روی تصویر بزن.
                        </p>
                    </div>
                </SectionCard>

                <SectionCard
                    title="تغییر رمز عبور"
                    description="رمز جدید حداقل ۴ کاراکتر باشد"
                    icon={LockResetIcon}
                    className="col-span-2 max-md:col-span-1"
                    index={2}
                >
                    <form
                        className="flex flex-col gap-xl"
                        onSubmit={handleSubmit(onSubmit)}
                        noValidate
                    >
                        <TextField
                            type="password"
                            label="رمز عبور فعلی"
                            variant="outlined"
                            value={password0}
                            error={Boolean(errors.password_old)}
                            helperText={errors.password_old?.message}
                            {...register("password_old", {
                                required: "رمز عبور فعلی را وارد کن",
                                minLength: {
                                    value: 4,
                                    message: "حداقل ۴ کاراکتر",
                                },
                            })}
                            onChange={(e) => setPassword0(e.target.value)}
                        />

                        <TextField
                            type="password"
                            label="رمز عبور جدید"
                            variant="outlined"
                            value={password}
                            error={Boolean(errors.password)}
                            helperText={errors.password?.message}
                            {...register("password", {
                                required: "رمز عبور جدید را وارد کن",
                                minLength: {
                                    value: 4,
                                    message: "حداقل ۴ کاراکتر",
                                },
                            })}
                            onChange={(e) => setPassword(e.target.value)}
                        />

                        <TextField
                            type="password"
                            label="تکرار رمز عبور جدید"
                            variant="outlined"
                            value={password2}
                            error={Boolean(errors.confirmPassword)}
                            helperText={errors.confirmPassword?.message}
                            {...register("confirmPassword", {
                                required: "تکرار رمز عبور را وارد کن",
                                validate: (value) =>
                                    value === password ||
                                    "با رمز عبور جدید یکسان نیست",
                            })}
                            onChange={(e) => setPassword2(e.target.value)}
                        />

                        <button
                            type="submit"
                            disabled={passChange.loading}
                            className="w-fit rounded-soft bg-primary px-2xl py-sm text-base text-white transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60 motion-reduce:transition-none"
                        >
                            {passChange.loading ? "در حال ثبت…" : "ثبت رمز جدید"}
                        </button>
                    </form>
                </SectionCard>
            </div>
        </div>
    );
};

export default Profile;
